// Definizione delle entità centralizzate per el-table-column-code-description
// Ogni entità ha ruoli, path, link e displayName unificati per tutto il progetto

// Definizione delle entità centralizzate per el-table-column-code-description
// Ogni entità ha ruoli, path, link e displayName unificati per tutto il progetto

import { ROLE_GROUPS, Role } from './roles';

/**
 * Interfaccia per la configurazione di un'entità
 */
export interface EntityPermission {
	/** Array dei ruoli autorizzati per questa entità */
	readonly roles: readonly Role[];
	/** Path base per il routing */
	readonly path: string;
	/** Link specifico per il routing */
	readonly link: string;
	/** Nome visualizzato nell'interfaccia (singolare) */
	readonly displayName: string;
	/** Nome visualizzato al plurale */
	readonly pluralName: string;
}

/**
 * Definizione delle chiavi delle entità come costanti per evitare typo e supportare autocomplete
 */
export const ENTITIES = {
	USERS: 'users',
	WORKERS: 'workers',
	CUSTOMERS: 'customers',
	JOB_ORDERS: 'jobOrders',
	JOB_ORDERS_MANAGERS: 'jobOrdersManagers',
	JOB_ORDERS_ALLOWED_WORKERS: 'jobOrdersAllowedWorkers',
	ACTIVITIES: 'activities',
	GROUPS: 'groups',
	PURCHASE_ORDERS: 'purchaseOrders',
	PROFIT_CENTERS: 'profitCenters',
	TASKS: 'tasks',
	COST_CENTERS: 'costCenters',
	HOLIDAYS: 'holidays',
	REFUNDS: 'refunds',
	REVENUES: 'revenues',
	MIRRORING_CONFIGURATION: 'mirroringConfiguration',
	MIRRORING_QUEUE: 'mirroringQueue',
	TIMECARD: 'timecard',
} as const;

/**
 * Tipo derivato per le chiavi delle entità
 */
export type EntityKey = (typeof ENTITIES)[keyof typeof ENTITIES];

/**
 * Tipo per la mappa completa delle configurazioni delle entità
 */
export type EntityPermissionsMap = Record<EntityKey, EntityPermission>;

/**
 * Configurazione centralizzata - immutabile
 * Basata sui pattern esistenti trovati nel codebase
 */
export const ENTITY_PERMISSIONS: EntityPermissionsMap = {
	[ENTITIES.USERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'users',
		displayName: 'Utente',
		pluralName: 'Utenti',
	},
	[ENTITIES.WORKERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'workers',
		displayName: 'Collaboratore',
		pluralName: 'Collaboratori',
	},
	[ENTITIES.CUSTOMERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'customers',
		displayName: 'Cliente',
		pluralName: 'Clienti',
	},
	[ENTITIES.JOB_ORDERS]: {
		roles: ROLE_GROUPS.MANAGEMENT_LEVEL,
		path: 'system',
		link: 'orders',
		displayName: 'Commessa',
		pluralName: 'Commesse',
	},
	[ENTITIES.JOB_ORDERS_MANAGERS]: {
		roles: ROLE_GROUPS.ADMIN_LEVEL,
		path: '',
		link: '',
		displayName: 'Responsabile Commessa',
		pluralName: 'Responsabili Commesse',
	},
	[ENTITIES.JOB_ORDERS_ALLOWED_WORKERS]: {
		roles: ROLE_GROUPS.ADMIN_LEVEL,
		path: '',
		link: '',
		displayName: 'Collaboratore',
		pluralName: 'Collaboratori',
	},
	[ENTITIES.ACTIVITIES]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'activities',
		displayName: 'Attività',
		pluralName: 'Attività',
	},
	[ENTITIES.GROUPS]: {
		roles: ROLE_GROUPS.ADMIN_LEVEL,
		path: 'system',
		link: 'groups',
		displayName: 'Gruppo',
		pluralName: 'Gruppi',
	},
	[ENTITIES.PURCHASE_ORDERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'purchaseOrders',
		displayName: 'Commessa BC',
		pluralName: 'Commesse BC',
	},
	[ENTITIES.PROFIT_CENTERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'profitCenters',
		displayName: 'Profit Center',
		pluralName: 'Profit Center',
	},
	[ENTITIES.TASKS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'tasks',
		displayName: 'Task',
		pluralName: 'Task',
	},
	[ENTITIES.COST_CENTERS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'costCenters',
		displayName: 'Centro di Costo',
		pluralName: 'Centri di Costo',
	},
	[ENTITIES.HOLIDAYS]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'system',
		link: 'holidays',
		displayName: 'Festività',
		pluralName: 'Festività',
	},
	[ENTITIES.REFUNDS]: {
		roles: ROLE_GROUPS.EVERYONE,
		path: 'refund',
		link: 'refunds',
		displayName: 'Rimborso',
		pluralName: 'Rimborsi',
	},
	[ENTITIES.REVENUES]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'economics',
		link: 'revenues',
		displayName: 'Costo/Ricavo',
		pluralName: 'Costi/Ricavi',
	},
	[ENTITIES.MIRRORING_CONFIGURATION]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'cross-company',
		link: 'configuration',
		displayName: 'Configurazione Mirroring',
		pluralName: 'Configurazioni Mirroring',
	},
	[ENTITIES.MIRRORING_QUEUE]: {
		roles: ROLE_GROUPS.SYS_ADMIN_ONLY,
		path: 'cross-company',
		link: 'mirroring',
		displayName: 'Coda Sincronizzazione',
		pluralName: 'Code Sincronizzazioni',
	},
	[ENTITIES.TIMECARD]: {
		roles: ROLE_GROUPS.EVERYONE,
		path: 'timecard',
		link: 'timecards',
		displayName: 'Timecard',
		pluralName: 'Timecard',
	},
} as const;

/**
 * Array delle chiavi delle entità per iterazione
 */
export const ENTITY_KEYS: readonly EntityKey[] = Object.values(ENTITIES);

/**
 * Ottiene la configurazione per un'entità specifica
 * @param entityKey - Chiave dell'entità (usare ENTITIES.*)
 * @returns Configurazione con roles, path, link, displayName
 * @throws Error Se l'entità non esiste
 */
export function getEntityPermissions(entityKey: EntityKey): EntityPermission {
	const config = ENTITY_PERMISSIONS[entityKey];

	if (!config) {
		throw new Error(
			`Entity "${entityKey}" not found in ENTITY_PERMISSIONS. ` +
				`Available entities: ${ENTITY_KEYS.join(', ')}`,
		);
	}

	return config;
}

/**
 * Verifica se un'entità esiste nella configurazione
 * @param entityKey - Chiave dell'entità da verificare
 * @returns true se esiste
 */
export function hasEntity(entityKey: string): entityKey is EntityKey {
	return entityKey in ENTITY_PERMISSIONS;
}

/**
 * Funzioni di utility per lavorare con le entità
 */

/**
 * Ottiene tutte le entità accessibili da un insieme di ruoli
 * @param userRoles - Ruoli dell'utente
 * @returns Array delle chiavi delle entità accessibili
 */
export function getAccessibleEntities(userRoles: readonly string[]): EntityKey[] {
	return ENTITY_KEYS.filter((entityKey) => {
		const config = ENTITY_PERMISSIONS[entityKey];
		return config.roles.some((role) => userRoles.includes(role));
	});
}

/**
 * Verifica se un utente può accedere a una specifica entità
 * @param userRoles - Ruoli dell'utente
 * @param entityKey - Chiave dell'entità da verificare
 * @returns true se l'utente può accedere all'entità
 */
export function canAccessEntity(userRoles: readonly string[], entityKey: EntityKey): boolean {
	const config = ENTITY_PERMISSIONS[entityKey];
	return config.roles.some((role) => userRoles.includes(role));
}

/**
 * Ottiene tutte le entità di un path specifico
 * @param path - Path da filtrare
 * @returns Array delle chiavi delle entità per quel path
 */
export function getEntitiesByPath(path: string): EntityKey[] {
	return ENTITY_KEYS.filter((entityKey) => {
		return ENTITY_PERMISSIONS[entityKey].path === path;
	});
}

/**
 * Ottiene il path completo per un'entità
 * @param entityKey - Chiave dell'entità
 * @returns Path completo nel formato "/path/link"
 */
export function getEntityFullPath(entityKey: EntityKey): string {
	const config = ENTITY_PERMISSIONS[entityKey];
	return `/${config.path}/${config.link}`;
}

/**
 * Ottiene tutte le entità accessibili da ruoli specifici, raggruppate per path
 * @param userRoles - Ruoli dell'utente
 * @returns Oggetto con path come chiave e array di entità come valore
 */
export function getAccessibleEntitiesByPath(
	userRoles: readonly string[],
): Record<string, EntityKey[]> {
	const accessibleEntities = getAccessibleEntities(userRoles);

	return accessibleEntities.reduce(
		(grouped, entityKey) => {
			const config = ENTITY_PERMISSIONS[entityKey];
			if (!grouped[config.path]) {
				grouped[config.path] = [];
			}
			grouped[config.path].push(entityKey);
			return grouped;
		},
		{} as Record<string, EntityKey[]>,
	);
}

/**
 * Helper per debugging - stampa tutte le entità e i loro permessi
 * @param prefix - Prefisso opzionale per il log
 */
export function logEntityPermissions(prefix: string = 'Entity Permissions'): void {
	if (process.env.NODE_ENV !== 'production') {
		console.group(prefix);
		ENTITY_KEYS.forEach((entityKey) => {
			const config = ENTITY_PERMISSIONS[entityKey];
			console.log(`📄 ${entityKey}:`, {
				displayName: config.displayName,
				pluralName: config.pluralName,
				path: getEntityFullPath(entityKey),
				roles: config.roles,
			});
		});
		console.groupEnd();
	}
}
